import 'bootstrap/dist/css/bootstrap.css';
import { ReactNode, useEffect, useState } from 'react';
import { Badge, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import Spinner from '../../shared/spinner';
import { Parametros } from '../../shared/model/parametros';
import ParametrosService from '../../services/parametrosService';
import IntegracaoService from '../../services/integracaoService';
import CustomAlert from '../../shared/customAlert';
import { useVerificaPermissao } from '../../shared/useVerificaPermissao';
import { BackOfficeEnum } from '../../shared/constantes/backofficeEnum';
import { ProcessadoraCartaoEnum } from '../../shared/constantes/processadoraCartaoEnum';
import OriginadorService from '../../services/originadorService';
import { ProtectedAction } from '../../AccessControl';
import { Originador } from '../../shared/model/originador';

interface Option {
  value: string;
  label: string;
}

interface FieldProps {
  label: string;
  children: ReactNode;
  md?: number;
}

interface SectionProps {
  title: string;
  children: ReactNode;
}

interface FluxoEtapa {
  status: string;
  label: string;
  ativo: boolean;
  obrigatorio?: boolean;
  docSign?: boolean;
  ordem?: number;
}

const opcoesSecuritizadora: Option[] = [
  { value: 'VORTX', label: 'Vortx' },
  { value: 'GABARITO', label: 'Gabarito' }
];

const regioesS3: Option[] = [
  { value: 'us-gov-west-1', label: 'AWS GovCloud (US)' },
  { value: 'us-gov-east-1', label: 'AWS GovCloud (US-East)' },
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-east-2', label: 'US East (Ohio)' },
  { value: 'us-west-1', label: 'US West (N. California)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'eu-west-1', label: 'EU (Ireland)' },
  { value: 'eu-west-2', label: 'EU (London)' },
  { value: 'eu-west-3', label: 'EU (Paris)' },
  { value: 'eu-central-1', label: 'EU (Frankfurt)' },
  { value: 'eu-north-1', label: 'EU (Stockholm)' },
  { value: 'eu-south-1', label: 'EU (Milan)' },
  { value: 'ap-east-1', label: 'Asia Pacific (Hong Kong)' },
  { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'ap-northeast-2', label: 'Asia Pacific (Seoul)' },
  { value: 'ap-northeast-3', label: 'Asia Pacific (Osaka)' },
  { value: 'sa-east-1', label: 'South America (Sao Paulo)' },
  { value: 'cn-north-1', label: 'China (Beijing)' },
  { value: 'cn-northwest-1', label: 'China (Ningxia)' },
  { value: 'ca-central-1', label: 'Canada (Central)' },
  { value: 'me-south-1', label: 'Middle East (Bahrain)' },
  { value: 'af-south-1', label: 'Africa (Cape Town)' },
  { value: 'us-iso-east-1', label: 'US ISO East' },
  { value: 'us-isob-east-1', label: 'US ISOB East (Ohio)' },
  { value: 'us-iso-west-1', label: 'US ISO West' }
];

const fieldClass = 'form-control form-control-sm brds';
const selectClass = 'form-select form-select-sm brds';
const normalizarBooleano = (valor: any) => valor === true || valor === 1 || valor === '1' || valor === 'true';

const fluxoGestaoPadrao: FluxoEtapa[] = [
  { status: 'PENDENTE_DADOS_COMPLEMENTARES', label: 'Dados complementares', ativo: true, obrigatorio: true },
  { status: 'PENDENTE_DOCUMENTACAO', label: 'Documentação', ativo: true, obrigatorio: true },
  { status: 'DOCUMENTACAO_PARCIAL', label: 'Documentação parcial', ativo: true },
  { status: 'PENDENTE_ANALISE', label: 'Análise', ativo: true, obrigatorio: true },
  { status: 'ANALISE_JURIDICA', label: 'Análise jurídica', ativo: false, docSign: true },
  { status: 'ENVIADO_ASSINATURA', label: 'Enviado para assinatura', ativo: false, docSign: true },
  { status: 'PENDENTE_APROVACAO', label: 'Aprovação geral', ativo: true, obrigatorio: true },
  { status: 'APROVADA', label: 'Aprovada', ativo: true, obrigatorio: true }
];

const montarFluxoGestao = (jsonFluxo: string | undefined, analiseJuridicaAtiva: boolean): FluxoEtapa[] => {
  const fallback = fluxoGestaoPadrao.map((etapa) => ({
    ...etapa,
    ativo: etapa.status === 'ANALISE_JURIDICA' || etapa.status === 'ENVIADO_ASSINATURA'
      ? analiseJuridicaAtiva
      : etapa.ativo
  }));

  if (!jsonFluxo) return fallback;

  try {
    const parsed = JSON.parse(jsonFluxo);
    const etapasJson = Array.isArray(parsed) ? parsed : parsed?.etapas;
    if (!Array.isArray(etapasJson)) return fallback;

    const porStatus = new Map(fluxoGestaoPadrao.map((etapa) => [etapa.status, etapa]));
    const etapas = etapasJson
      .map((etapa: any) => {
        const base = porStatus.get(etapa.status);
        return base ? { ...base, ativo: normalizarBooleano(etapa.ativo) } : null;
      })
      .filter(Boolean) as FluxoEtapa[];

    fluxoGestaoPadrao.forEach((etapa) => {
      if (!etapas.some((item) => item.status === etapa.status)) {
        etapas.push({ ...etapa });
      }
    });

    return etapas.length > 0 ? etapas : fallback;
  } catch {
    return fallback;
  }
};

const normalizarEtapasFluxoGestao = (etapas: FluxoEtapa[]): FluxoEtapa[] => (
  etapas.map((etapa, index) => ({
    ...etapa,
    ativo: etapa.obrigatorio ? true : etapa.ativo,
    ordem: index + 1
  }))
);

const serializarFluxoGestao = (etapas: FluxoEtapa[]) => JSON.stringify({
  etapas: etapas.map(({ status, label, ativo, ordem }) => ({ status, label, ativo, ordem }))
});

const styles = {
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid #dee2e6'
  },
  section: {
    backgroundColor: '#fff',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px'
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    marginBottom: '14px'
  },
  subsection: {
    paddingTop: '12px',
    marginTop: '6px',
    borderTop: '1px solid #dee2e6'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    paddingTop: '8px',
    marginBottom: '12px'
  }
};

function Field({ label, children, md = 3 }: FieldProps) {
  return (
    <Col md={md} className="mb-3">
      <Form.Label className="mb-1 fw-semibold">{label}</Form.Label>
      {children}
    </Col>
  );
}

function Section({ title, children }: SectionProps) {
  return (
    <section style={styles.section}>
      <h5 style={styles.sectionTitle}>{title}</h5>
      {children}
    </section>
  );
}

function ParametrosTi() {
  useVerificaPermissao();

  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(true);

  const [nomeBancoBpo, setNomeBancoBpo] = useState('');
  const [urlBpo, setUrlBpo] = useState('');
  const [usuarioBpo, setUsuarioBpo] = useState('');
  const [senhaBpo, setSenhaBpo] = useState('');
  const [urlDigitalBpo, setUrlDigitalBpo] = useState('');
  const [urlLecca, setUrlLecca] = useState('');
  const [tokenLecca, setTokenLecca] = useState('');
  const [urlVortx, setUrlVortx] = useState('');
  const [tokenVortx, setTokenVortx] = useState('');
  const [urlCessao, setUrlCessao] = useState('');
  const [tokenCessao, setTokenCessao] = useState('');

  const [integraS3, setIntegraS3] = useState(false);
  const [accessKeyS3, setAccessKeyS3] = useState('');
  const [secretKeyS3, setSecretKeyS3] = useState('');
  const [bucketS3, setBucketS3] = useState('');
  const [pastaDocumentosS3, setPastaDocumentosS3] = useState('');
  const [pastaComunicadosS3, setPastaComunicadosS3] = useState('');
  const [localizacaoS3, setLocalizacaoS3] = useState('');

  const [tipoIntegracaoSecuritizadora, setTipoIntegracaoSecuritizadora] = useState('');
  const [accessKeyGabaritoS3, setAccessKeyGabaritoS3] = useState('');
  const [secretKeyGabaritoS3, setSecretKeyGabaritoS3] = useState('');
  const [bucketGabaritoS3, setBucketGabaritoS3] = useState('');
  const [pastaDocumentosGabaritoS3, setPastaDocumentosGabaritoS3] = useState('');
  const [pastaComunicadosGabaritoS3, setPastaComunicadosGabaritoS3] = useState('');
  const [localizacaoGabaritoS3, setLocalizacaoGabaritoS3] = useState('');

  const [cnpjBancarizadoraGabarito, setCnpjBancarizadoraGabarito] = useState('');
  const [nomeBancarizadoraGabarito, setNomeBancarizadoraGabarito] = useState('');
  const [agioOriginadorGabarito, setAgioOriginadorGabarito] = useState<number | ''>('');
  const [custoTedGabarito, setCustoTedGabarito] = useState<number | ''>('');
  const [taxaBancarizadoraGabarito, setTaxaBancarizadoraGabarito] = useState<number | ''>('');

  const [idOriginador, setIdOriginador] = useState<number | string>('');
  const [originadoresList, setOriginadoresList] = useState<any[]>([]);
  const [chaveApiPh3a, setChaveApiPh3a] = useState('');
  const [serviceAccountIss, setServiceAccountIss] = useState('');
  const [urlDeAutenticacao, setUrlDeAutenticacao] = useState('');
  const [urlApi, setUrlApi] = useState('');

  const [backoffice, setBackoffice] = useState('');
  const [processadoraCartao, setProcessadoraCartao] = useState('');
  const [loginCpf, setLoginCpf] = useState<number>(0);
  const [gestaoPromotora, setGestaoPromotora] = useState(false);
  const [gestaoPromotoraAnaliseJuridica, setGestaoPromotoraAnaliseJuridica] = useState(false);
  const [gestaoPromotoraFluxoStatusJson, setGestaoPromotoraFluxoStatusJson] = useState('');
  const [fluxoGestaoEtapas, setFluxoGestaoEtapas] = useState<FluxoEtapa[]>(fluxoGestaoPadrao);
  const [fluxoModalOpen, setFluxoModalOpen] = useState(false);
  const [docSignAtivo, setDocSignAtivo] = useState(false);
  const [usuarioFuncao, setUsuarioFuncao] = useState('');

  const parametrosService = new ParametrosService();
  const integracaoService = new IntegracaoService();
  const originadorService = new OriginadorService();
  const { REACT_APP_PERFIL } = process.env;

  const ocultarLeccaEGabarito = REACT_APP_PERFIL === 'QISTA' || REACT_APP_PERFIL === 'FICTOR';
  const exibirGabarito = !ocultarLeccaEGabarito && tipoIntegracaoSecuritizadora === 'GABARITO';

  const listarTodosParametrosTi = async () => {
    try {
      setLoading(true);
      const responseList = await parametrosService.listarTodosParametros();
      const json = responseList.data;

      setNomeBancoBpo(json.nomeBancoBpo || '');
      setUrlBpo(json.urlBpo || '');
      setUsuarioBpo(json.usuarioBpo || '');
      setSenhaBpo(json.senhaBpo || '');
      setUrlDigitalBpo(json.urlDigitalBpo || '');
      setUrlLecca(json.urlLecca || '');
      setTokenLecca(json.tokenLecca || '');
      setUrlVortx(json.urlVortx || '');
      setTokenVortx(json.tokenVortx || '');
      setUrlCessao(json.urlCessao || '');
      setTokenCessao(json.tokenCessao || '');

      setIntegraS3(Boolean(json.integraS3));
      setAccessKeyS3(json.accessKeyS3 || '');
      setSecretKeyS3(json.secretKeyS3 || '');
      setBucketS3(json.bucketS3 || '');
      setPastaDocumentosS3(json.pastaDocumentosS3 || '');
      setPastaComunicadosS3(json.pastaComunicadosS3 || '');
      setLocalizacaoS3(json.localizacaoS3 || '');

      setTipoIntegracaoSecuritizadora(json.tipoIntegracaoSecuritizadora || '');
      setAccessKeyGabaritoS3(json.accessKeyGabaritoS3 || '');
      setSecretKeyGabaritoS3(json.secretKeyGabaritoS3 || '');
      setBucketGabaritoS3(json.bucketGabaritoS3 || '');
      setPastaDocumentosGabaritoS3(json.pastaDocumentosGabaritoS3 || '');
      setPastaComunicadosGabaritoS3(json.pastaComunicadosGabaritoS3 || '');
      setLocalizacaoGabaritoS3(json.localizacaoGabaritoS3 || '');

      setCnpjBancarizadoraGabarito(json.cnpjBancarizadoraGabarito || '');
      setNomeBancarizadoraGabarito(json.nomeBancarizadoraGabarito || '');
      setAgioOriginadorGabarito(json.agioOriginadorGabarito ?? '');
      setCustoTedGabarito(json.custoTedGabarito ?? '');
      setTaxaBancarizadoraGabarito(json.taxaBancarizadoraGabarito ?? '');

      setBackoffice(json.backoffice || '');
      setProcessadoraCartao(json.processadoraCartao || '');
      setLoginCpf(json.login_cpf ?? 0);
      const analiseJuridica = normalizarBooleano(json.gestaoPromotoraAnaliseJuridica ?? json.gestao_promotora_analise_juridica);
      const fluxoJson = json.gestaoPromotoraFluxoStatusJson ?? json.gestao_promotora_fluxo_status_json ?? '';
      setGestaoPromotora(normalizarBooleano(json.gestaoPromotora ?? json.gestao_promotora));
      setGestaoPromotoraAnaliseJuridica(analiseJuridica);
      setGestaoPromotoraFluxoStatusJson(fluxoJson);
      setFluxoGestaoEtapas(montarFluxoGestao(fluxoJson, analiseJuridica));

      setIdOriginador(json.idOriginador || '');
      setChaveApiPh3a(json.chaveApiPh3a || '');
      setServiceAccountIss(json.serviceAccountIss || '');
      setUrlDeAutenticacao(json.urlDeAutenticacao || '');
      setUrlApi(json.urlApi || '');
      setUsuarioFuncao(json.usuarioFuncao || '');
    } catch (err: any) {
      console.error(err.response?.data || err.message);
      setAlert({ message: err.response?.data || err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const carregarDocSignAtivo = async () => {
    try {
      const response = await integracaoService.verificarIntegracaoAtiva('DOCSIGN');
      const ativo = normalizarBooleano(response.data);
      setDocSignAtivo(ativo);
      return ativo;
    } catch {
      setDocSignAtivo(false);
      return false;
    }
  };

  const listarOriginadores = async () => {
    try {
      const response = await originadorService.listarTodosOriginadoresAtivos();
      const data = response.data;
      setOriginadoresList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erro ao buscar originadores", err);
      setAlert({ message: 'Não foi possível carregar a lista de originadores.', type: 'error' });
      setOriginadoresList([]);
    }
  };

  const montarParametros = (etapasOverride?: FluxoEtapa[]) => {
    const etapasFluxo = normalizarEtapasFluxoGestao(etapasOverride || fluxoGestaoEtapas);
    const analiseJuridicaAtiva = etapasFluxo.some((etapa) =>
      ['ANALISE_JURIDICA', 'ENVIADO_ASSINATURA'].includes(etapa.status) && etapa.ativo
    );

    const parametros: any = new Parametros();
    parametros.nomeBancoBpo = nomeBancoBpo;
    parametros.urlBpo = urlBpo;
    parametros.usuarioBpo = usuarioBpo;
    parametros.senhaBpo = senhaBpo;
    parametros.urlDigitalBpo = urlDigitalBpo;
    parametros.urlLecca = urlLecca;
    parametros.tokenLecca = tokenLecca;
    parametros.urlVortx = urlVortx;
    parametros.tokenVortx = tokenVortx;
    parametros.urlCessao = urlCessao;
    parametros.tokenCessao = tokenCessao;
    parametros.idOriginador = idOriginador ? Number(idOriginador) : null;
    parametros.chaveApiPh3a = chaveApiPh3a;
    parametros.serviceAccountIss = serviceAccountIss;
    parametros.urlDeAutenticacao = urlDeAutenticacao;
    parametros.urlApi = urlApi;

    parametros.integraS3 = integraS3;
    parametros.accessKeyS3 = accessKeyS3;
    parametros.secretKeyS3 = secretKeyS3;
    parametros.bucketS3 = bucketS3;
    parametros.pastaDocumentosS3 = pastaDocumentosS3;
    parametros.pastaComunicadosS3 = pastaComunicadosS3;
    parametros.localizacaoS3 = localizacaoS3;

    parametros.tipoIntegracaoSecuritizadora = tipoIntegracaoSecuritizadora;
    parametros.accessKeyGabaritoS3 = accessKeyGabaritoS3;
    parametros.secretKeyGabaritoS3 = secretKeyGabaritoS3;
    parametros.bucketGabaritoS3 = bucketGabaritoS3;
    parametros.pastaDocumentosGabaritoS3 = pastaDocumentosGabaritoS3;
    parametros.pastaComunicadosGabaritoS3 = pastaComunicadosGabaritoS3;
    parametros.localizacaoGabaritoS3 = localizacaoGabaritoS3;

    parametros.cnpjBancarizadoraGabarito = cnpjBancarizadoraGabarito;
    parametros.nomeBancarizadoraGabarito = nomeBancarizadoraGabarito;
    parametros.agioOriginadorGabarito = agioOriginadorGabarito !== '' ? Number(agioOriginadorGabarito) : null;
    parametros.custoTedGabarito = custoTedGabarito !== '' ? Number(custoTedGabarito) : null;
    parametros.taxaBancarizadoraGabarito = taxaBancarizadoraGabarito !== '' ? Number(taxaBancarizadoraGabarito) : null;

    parametros.backoffice = backoffice;
    parametros.processadoraCartao = processadoraCartao;
    parametros.login_cpf = loginCpf;
    parametros.gestaoPromotora = gestaoPromotora;
    parametros.gestaoPromotoraAnaliseJuridica = gestaoPromotora && analiseJuridicaAtiva;
    parametros.gestaoPromotoraFluxoStatusJson = gestaoPromotora ? serializarFluxoGestao(etapasFluxo) : '';
    parametros.usuarioFuncao = backoffice === 'FUNCAO' ? usuarioFuncao : '';

    return parametros;
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);

    try {
      await carregarDocSignAtivo();
      const response = await parametrosService.alterarParametros(montarParametros());
      setGestaoPromotora(normalizarBooleano(response.data?.gestaoPromotora ?? response.data?.gestao_promotora));
      const analiseJuridica = normalizarBooleano(response.data?.gestaoPromotoraAnaliseJuridica ?? response.data?.gestao_promotora_analise_juridica);
      const fluxoJson = response.data?.gestaoPromotoraFluxoStatusJson ?? response.data?.gestao_promotora_fluxo_status_json ?? gestaoPromotoraFluxoStatusJson;
      setGestaoPromotoraAnaliseJuridica(analiseJuridica);
      setGestaoPromotoraFluxoStatusJson(fluxoJson);
      setFluxoGestaoEtapas(montarFluxoGestao(fluxoJson, analiseJuridica));
      setAlert({ message: 'Alteração realizada com sucesso!', type: 'success' });
    } catch (err: any) {
      setAlert({ message: err.response?.data || err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const testarConexaoS3 = async (e: any) => {
    e.preventDefault();
    if (integraS3 && (accessKeyS3 === '' || secretKeyS3 === '' || bucketS3 === '' || pastaDocumentosS3 === '' || pastaComunicadosS3 === '')) {
      setAlert({ message: 'Preencha todos os campos de credenciais da AWS', type: 'error' });
      return;
    }

    try {
      setLoading(true);
      const parametros: Parametros = new Parametros();
      parametros.integraS3 = integraS3;
      parametros.accessKeyS3 = accessKeyS3;
      parametros.secretKeyS3 = secretKeyS3;
      parametros.bucketS3 = bucketS3;
      parametros.pastaDocumentosS3 = pastaDocumentosS3;
      parametros.pastaComunicadosS3 = pastaComunicadosS3;
      parametros.localizacaoS3 = localizacaoS3;

      const response = await parametrosService.verificarConexaoS3(parametros);
      setAlert({ message: response.data, type: 'success' });
    } catch (err: any) {
      setAlert({ message: err.response?.data || err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const limparCamposS3Principal = () => {
    setAccessKeyS3('');
    setSecretKeyS3('');
    setBucketS3('');
    setPastaDocumentosS3('');
    setPastaComunicadosS3('');
    setLocalizacaoS3('');
  };

  const handleCheckboxChange = () => {
    const novoValor = !integraS3;
    setIntegraS3(novoValor);
    if (!novoValor) {
      limparCamposS3Principal();
    }
  };

  const alternarEtapaFluxo = (status: string, ativo: boolean) => {
    setFluxoGestaoEtapas((prev) => prev.map((etapa) => {
      if (['ANALISE_JURIDICA', 'ENVIADO_ASSINATURA'].includes(status)
          && ['ANALISE_JURIDICA', 'ENVIADO_ASSINATURA'].includes(etapa.status)) {
        return { ...etapa, ativo };
      }
      if (etapa.status === status) {
        return { ...etapa, ativo };
      }
      return etapa;
    }));
  };

  const moverEtapaFluxo = (index: number, direcao: -1 | 1) => {
    setFluxoGestaoEtapas((prev) => {
      const destino = index + direcao;
      if (destino < 0 || destino >= prev.length) return prev;
      const atual = [...prev];
      [atual[index], atual[destino]] = [atual[destino], atual[index]];
      return atual;
    });
  };

  const salvarFluxoGestao = async () => {
    const etapasNormalizadas = normalizarEtapasFluxoGestao(fluxoGestaoEtapas);
    const analiseJuridicaAtiva = etapasNormalizadas.some((etapa) =>
      ['ANALISE_JURIDICA', 'ENVIADO_ASSINATURA'].includes(etapa.status) && etapa.ativo
    );
    const fluxoJson = serializarFluxoGestao(etapasNormalizadas);

    setLoading(true);
    try {
      const response = await parametrosService.alterarParametros(montarParametros(etapasNormalizadas));
      const json = response.data || {};
      const analiseJuridicaSalva = normalizarBooleano(
        json.gestaoPromotoraAnaliseJuridica ?? json.gestao_promotora_analise_juridica ?? analiseJuridicaAtiva
      );
      const fluxoJsonSalvo = json.gestaoPromotoraFluxoStatusJson ?? json.gestao_promotora_fluxo_status_json ?? fluxoJson;

      setGestaoPromotora(normalizarBooleano(json.gestaoPromotora ?? json.gestao_promotora ?? gestaoPromotora));
      setGestaoPromotoraAnaliseJuridica(analiseJuridicaSalva);
      setGestaoPromotoraFluxoStatusJson(fluxoJsonSalvo);
      setFluxoGestaoEtapas(normalizarEtapasFluxoGestao(montarFluxoGestao(fluxoJsonSalvo, analiseJuridicaSalva)));
      setFluxoModalOpen(false);
      setAlert({ message: 'Fluxo da gestão salvo com sucesso!', type: 'success' });
    } catch (err: any) {
      const mensagem = typeof err.response?.data === 'object'
        ? err.response?.data?.mensagem || err.response?.data?.message
        : err.response?.data;
      setAlert({ message: mensagem || err.message || 'Erro ao salvar fluxo da gestão.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const abrirFluxoGestao = async () => {
    await carregarDocSignAtivo();
    const etapas = normalizarEtapasFluxoGestao(
      montarFluxoGestao(gestaoPromotoraFluxoStatusJson, gestaoPromotoraAnaliseJuridica)
    );
    setFluxoGestaoEtapas(etapas);
    setFluxoModalOpen(true);
  };

  useEffect(() => {
    listarTodosParametrosTi();
    listarOriginadores();
    carregarDocSignAtivo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="position-relative m-md-3 container-fluid SiteFontSize">
      {alert && (<CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />)}

      <div style={styles.pageHeader}>
        <h6 className="mb-0"><strong>Sistema &gt; Parâmetros TI</strong></h6>
      </div>

      {loading ? <Spinner loading={loading} /> : (
        <Form onSubmit={handleSubmit}>
          <Section title="Configurações Gerais">
            <Row>
              <Field label="BackOffice">
                <Form.Select className={selectClass} value={backoffice} onChange={(e) => setBackoffice(e.target.value)}>
                  <option value="" disabled>Selecione uma opção</option>
                  {BackOfficeEnum.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </Form.Select>
              </Field>

              <Field label="Processadora Cartão">
                <Form.Select className={selectClass} value={processadoraCartao} onChange={(e) => setProcessadoraCartao(e.target.value)}>
                  <option value="" disabled>Selecione uma opção</option>
                  {ProcessadoraCartaoEnum.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </Form.Select>
              </Field>

              <Field label="Tipo de Login">
                <Form.Select className={selectClass} value={loginCpf} onChange={(e) => setLoginCpf(Number(e.target.value))}>
                  <option value={0}>Usuário</option>
                  <option value={1}>CPF</option>
                </Form.Select>
              </Field>

              <Field label="Gestão de Promotora">
                <Form.Select className={selectClass} value={gestaoPromotora ? '1' : '0'} onChange={(e) => setGestaoPromotora(e.target.value === '1')}>
                  <option value="0">Desativada</option>
                  <option value="1">Ativada</option>
                </Form.Select>
              </Field>

              {gestaoPromotora && (
                <Field label="Fluxo da Gestão">
                  <Button type="button" variant="outline-secondary" className="w-100" onClick={abrirFluxoGestao}>
                    Configurar fluxo
                  </Button>
                </Field>
              )}

              {backoffice === 'FUNCAO' && (
                <Field label="Usuário Função" md={4}>
                  <Form.Control className={fieldClass} type="text" value={usuarioFuncao} onChange={(e) => setUsuarioFuncao(e.target.value)} />
                </Field>
              )}
            </Row>
          </Section>

          <Section title="Integração BPO">
            <Row>
              <Field label="Nome Banco BPO" md={4}>
                <Form.Control className={fieldClass} type="text" value={nomeBancoBpo} onChange={(e) => setNomeBancoBpo(e.target.value)} />
              </Field>

              <Field label="Usuário" md={2}>
                <Form.Control className={fieldClass} type="text" value={usuarioBpo} onChange={(e) => setUsuarioBpo(e.target.value)} />
              </Field>

              <Field label="Senha" md={2}>
                <Form.Control className={fieldClass} type="password" value={senhaBpo} onChange={(e) => setSenhaBpo(e.target.value)} />
              </Field>

              <Field label="URL Serviço" md={4}>
                <Form.Control className={fieldClass} type="url" value={urlBpo} onChange={(e) => setUrlBpo(e.target.value)} />
              </Field>

              <Field label="URL Digital BPO" md={4}>
                <Form.Control className={fieldClass} type="url" value={urlDigitalBpo} onChange={(e) => setUrlDigitalBpo(e.target.value)} />
              </Field>
            </Row>
          </Section>

          <Section title="Cessão e Securitizadora">
            <Row>
              {!ocultarLeccaEGabarito && (
                <>
                  <Field label="URL Lecca" md={4}>
                    <Form.Control className={fieldClass} type="url" value={urlLecca} onChange={(e) => setUrlLecca(e.target.value)} />
                  </Field>

                  <Field label="Token Lecca" md={4}>
                    <Form.Control className={fieldClass} type="password" value={tokenLecca} onChange={(e) => setTokenLecca(e.target.value)} />
                  </Field>

                  <Field label="Tipo de Cessão" md={4}>
                    <Form.Select className={selectClass} value={tipoIntegracaoSecuritizadora} onChange={(e) => setTipoIntegracaoSecuritizadora(e.target.value)}>
                      <option value="" disabled>Selecione uma opção</option>
                      {opcoesSecuritizadora.map((item) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                      ))}
                    </Form.Select>
                  </Field>
                </>
              )}

              <Field label="URL VortX" md={4}>
                <Form.Control className={fieldClass} type="url" value={urlVortx} onChange={(e) => setUrlVortx(e.target.value)} />
              </Field>

              <Field label="Token VortX" md={4}>
                <Form.Control className={fieldClass} type="password" value={tokenVortx} onChange={(e) => setTokenVortx(e.target.value)} />
              </Field>

              <Field label="URL Cessão" md={4}>
                <Form.Control className={fieldClass} type="url" value={urlCessao} onChange={(e) => setUrlCessao(e.target.value)} />
              </Field>

              <Field label="Token Cessão" md={4}>
                <Form.Control className={fieldClass} type="password" value={tokenCessao} onChange={(e) => setTokenCessao(e.target.value)} />
              </Field>
            </Row>

            {exibirGabarito && (
              <div style={styles.subsection}>
                <h6 className="fw-bold mb-3">Gabarito</h6>
                <Row>
                  <Field label="Access Key" md={3}>
                    <Form.Control className={fieldClass} type="text" value={accessKeyGabaritoS3} onChange={(e) => setAccessKeyGabaritoS3(e.target.value)} />
                  </Field>

                  <Field label="Secret Key" md={3}>
                    <Form.Control className={fieldClass} type="password" value={secretKeyGabaritoS3} onChange={(e) => setSecretKeyGabaritoS3(e.target.value)} />
                  </Field>

                  <Field label="Nome do Bucket" md={3}>
                    <Form.Control className={fieldClass} type="text" value={bucketGabaritoS3} onChange={(e) => setBucketGabaritoS3(e.target.value)} />
                  </Field>

                  <Field label="Pasta Documentos S3" md={3}>
                    <Form.Control className={fieldClass} type="text" value={pastaDocumentosGabaritoS3} onChange={(e) => setPastaDocumentosGabaritoS3(e.target.value)} />
                  </Field>

                  <Field label="Pasta Comunicados S3" md={3}>
                    <Form.Control className={fieldClass} type="text" value={pastaComunicadosGabaritoS3} onChange={(e) => setPastaComunicadosGabaritoS3(e.target.value)} />
                  </Field>

                  <Field label="Localização S3" md={3}>
                    <Form.Select className={selectClass} value={localizacaoGabaritoS3} onChange={(e) => setLocalizacaoGabaritoS3(e.target.value)}>
                      <option value="">Selecione uma região...</option>
                      {regioesS3.map((item) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                      ))}
                    </Form.Select>
                  </Field>

                  <Field label="CNPJ Bancarizadora" md={3}>
                    <Form.Control className={fieldClass} type="text" value={cnpjBancarizadoraGabarito} onChange={(e) => setCnpjBancarizadoraGabarito(e.target.value)} />
                  </Field>

                  <Field label="Nome Bancarizadora" md={3}>
                    <Form.Control className={fieldClass} type="text" value={nomeBancarizadoraGabarito} onChange={(e) => setNomeBancarizadoraGabarito(e.target.value)} />
                  </Field>

                  <Field label="Ágio Originador (%)" md={3}>
                    <Form.Control className={fieldClass} type="number" step="0.01" value={agioOriginadorGabarito} onChange={(e) => setAgioOriginadorGabarito(e.target.value !== '' ? Number(e.target.value) : '')} />
                  </Field>

                  <Field label="Custo TED (R$)" md={3}>
                    <Form.Control className={fieldClass} type="number" step="0.01" value={custoTedGabarito} onChange={(e) => setCustoTedGabarito(e.target.value !== '' ? Number(e.target.value) : '')} />
                  </Field>

                  <Field label="Taxa Bancarizadora (%)" md={3}>
                    <Form.Control className={fieldClass} type="number" step="0.01" value={taxaBancarizadoraGabarito} onChange={(e) => setTaxaBancarizadoraGabarito(e.target.value !== '' ? Number(e.target.value) : '')} />
                  </Field>
                </Row>
              </div>
            )}
          </Section>

          <Section title="Integração AWS S3 Principal">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <Form.Check
                type="switch"
                id="integraS3"
                label="Ativar Integração AWS S3"
                checked={integraS3}
                onChange={handleCheckboxChange}
              />

              <Button type="button" id="App-button-cor-principal-escura" size="sm" onClick={testarConexaoS3} disabled={!integraS3}>
                Testar Conexão
              </Button>
            </div>

            {integraS3 && (
              <Row>
                <Field label="Access Key" md={3}>
                  <Form.Control className={fieldClass} type="text" value={accessKeyS3} onChange={(e) => setAccessKeyS3(e.target.value)} />
                </Field>

                <Field label="Secret Key" md={3}>
                  <Form.Control className={fieldClass} type="password" value={secretKeyS3} onChange={(e) => setSecretKeyS3(e.target.value)} />
                </Field>

                <Field label="Nome do Bucket" md={3}>
                  <Form.Control className={fieldClass} type="text" value={bucketS3} onChange={(e) => setBucketS3(e.target.value)} />
                </Field>

                <Field label="Pasta Documentos S3" md={3}>
                  <Form.Control className={fieldClass} type="text" value={pastaDocumentosS3} onChange={(e) => setPastaDocumentosS3(e.target.value)} />
                </Field>

                <Field label="Pasta Comunicados S3" md={3}>
                  <Form.Control className={fieldClass} type="text" value={pastaComunicadosS3} onChange={(e) => setPastaComunicadosS3(e.target.value)} />
                </Field>

                <Field label="Localização S3" md={3}>
                  <Form.Select className={selectClass} value={localizacaoS3} onChange={(e) => setLocalizacaoS3(e.target.value)}>
                    <option value="">Selecione uma região...</option>
                    {regioesS3.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </Form.Select>
                </Field>
              </Row>
            )}
          </Section>

          <Section title={`${Originador.DESCRITIVO_GERAL} e PH3A`}>
            <Row>
              <Field label={`Selecione a ${Originador.DESCRITIVO_GERAL}`} md={4}>
                <Form.Select className={selectClass} value={idOriginador} onChange={(e) => setIdOriginador(e.target.value)}>
                  <option value="">Selecione uma opção</option>
                  {Array.isArray(originadoresList) && originadoresList.map((originador) => (
                    <option key={originador.id} value={originador.id}>
                      {originador.nome}
                    </option>
                  ))}
                </Form.Select>
              </Field>

              <Field label="Chave API PH3A" md={2}>
                <Form.Control className={fieldClass} type="password" value={chaveApiPh3a} onChange={(e) => setChaveApiPh3a(e.target.value)} />
              </Field>

              <Field label="Service Account Iss" md={2}>
                <Form.Control className={fieldClass} type="password" value={serviceAccountIss} onChange={(e) => setServiceAccountIss(e.target.value)} />
              </Field>

              <Field label="URL De Autenticação" md={2}>
                <Form.Control className={fieldClass} type="text" value={urlDeAutenticacao} onChange={(e) => setUrlDeAutenticacao(e.target.value)} />
              </Field>

              <Field label="URL API" md={2}>
                <Form.Control className={fieldClass} type="text" value={urlApi} onChange={(e) => setUrlApi(e.target.value)} />
              </Field>
            </Row>
          </Section>

          <ProtectedAction>
            <div style={styles.actions}>
              <Button type="button" id="App-button-cor-principal" onClick={() => window.history.back()}>
                Voltar
              </Button>
              <Button type="submit" className="btn btn-success">
                Salvar Parâmetros
              </Button>
            </div>
          </ProtectedAction>

          <Modal show={fluxoModalOpen} onHide={() => setFluxoModalOpen(false)} size="xl" centered>
            <Modal.Header closeButton>
              <Modal.Title>Fluxo da Gestão Promotora</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="g-3">
                <Col lg={8}>
                  <div className="border rounded bg-white">
                    {[
                      { lane: 'Cadastro', statuses: ['PENDENTE_DADOS_COMPLEMENTARES', 'PENDENTE_DOCUMENTACAO', 'DOCUMENTACAO_PARCIAL'] },
                      { lane: 'Análise', statuses: ['PENDENTE_ANALISE', 'ANALISE_JURIDICA', 'ENVIADO_ASSINATURA'] },
                      { lane: 'Aprovação', statuses: ['PENDENTE_APROVACAO', 'APROVADA'] }
                    ].map((linha) => {
                      const etapasLinha = fluxoGestaoEtapas.filter((etapa) => linha.statuses.includes(etapa.status));
                      return (
                        <div key={linha.lane} className="d-flex border-bottom" style={{ minHeight: 118 }}>
                          <div className="d-flex align-items-center justify-content-center border-end bg-light fw-semibold" style={{ width: 120 }}>
                            {linha.lane}
                          </div>
                          <div className="d-flex align-items-center flex-wrap p-3" style={{ gap: 12, flex: 1 }}>
                            {etapasLinha.map((etapa, index) => (
                              <div key={etapa.status} className="d-flex align-items-center">
                                <div
                                  className={`border rounded p-3 ${etapa.ativo ? 'bg-white' : 'bg-light text-muted'}`}
                                  style={{ width: 170, minHeight: 72, boxShadow: etapa.ativo ? '0 1px 2px rgba(0,0,0,.08)' : 'none' }}
                                >
                                  <div className="fw-semibold">{etapa.label}</div>
                                  <Badge bg={etapa.ativo ? 'success' : 'secondary'} className="mt-2">
                                    {etapa.ativo ? 'Ativo' : 'Inativo'}
                                  </Badge>
                                </div>
                                {index < etapasLinha.length - 1 && (
                                  <div className="mx-2 text-muted fw-bold">-&gt;</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Col>

                <Col lg={4}>
                  {!docSignAtivo && (
                    <div className="alert alert-warning py-2">
                      Integração DocSign inativa.
                    </div>
                  )}
                  <div className="border rounded p-3 bg-white">
                    <h6 className="fw-bold mb-3">Ordem dos status</h6>
                    {fluxoGestaoEtapas.map((etapa, index) => (
                      <div key={etapa.status} className="d-flex align-items-center justify-content-between border-bottom py-2">
                        <div>
                          <div className="fw-semibold">{index + 1}. {etapa.label}</div>
                          {etapa.docSign && <small className="text-muted">DocSign</small>}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Form.Check
                            type="switch"
                            checked={etapa.obrigatorio || etapa.ativo}
                            disabled={etapa.obrigatorio}
                            onChange={(e) => alternarEtapaFluxo(etapa.status, e.target.checked)}
                          />
                          <Button type="button" size="sm" variant="outline-secondary" title="Subir status" onClick={() => moverEtapaFluxo(index, -1)} disabled={index === 0}>
                            <FontAwesomeIcon icon={faArrowUp} />
                          </Button>
                          <Button type="button" size="sm" variant="outline-secondary" title="Descer status" onClick={() => moverEtapaFluxo(index, 1)} disabled={index === fluxoGestaoEtapas.length - 1}>
                            <FontAwesomeIcon icon={faArrowDown} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="secondary" onClick={() => setFluxoModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="button" id="App-button-cor-principal-escura" onClick={salvarFluxoGestao} disabled={loading}>
                {loading ? 'Salvando...' : 'Salvar fluxo'}
              </Button>
            </Modal.Footer>
          </Modal>
        </Form>
      )}
    </div>
  );
}

export default ParametrosTi;
